import React, { useState, useEffect } from "react";
import { useEscapeClose, getDataWithJoin, getAlert, getDataWithComp } from "../../../utils/SamuiUtils";
import { useModalKeyboardNavigation, getKeyboardInstructions } from "../../../utils/ModalKeyboardNavigation";
import ItemModalAddItem from "./ItemModalAddItem";
import { normalizeImageValue, buildImageSrc } from "./ItemModalImageUtils";
import ItemModalView from "./ItemModalView";

const ItemModal = ({
  formDetailList,
  showItemModal,
  handleItemClose,
  itemDataList,
  onRowSelectItem,
  setItemDataList,
}) => {
  const CONTROL_HEIGHT = "calc(1.5em + .75rem + 2px)";

  const [searchTerm, setSearchTerm] = useState("");
  const [normalizedItemDataList, setNormalizedItemDataList] = useState(
    Array.isArray(itemDataList)
      ? itemDataList.map((item) => ({
        ...item,
        Item_Img: normalizeImageValue(item.Item_Img),
      }))
      : []
  );
  const [filteredItemDataList, setFilteredItemDataList] =
    useState(normalizedItemDataList);
  const [selectedItems, setSelectedItems] = useState([]);
  const [selectedRowIndex, setSelectedRowIndex] = useState(0);
  const [itemGroups, setItemGroups] = useState([]);
  const [selectedGroup, setSelectedGroup] = useState("");
  const [showAddItemModal, setShowAddItemModal] = useState(false);
  const [addFormData, setAddFormData] = useState({});

  useEscapeClose(showItemModal, handleItemClose);

  // Custom handler สำหรับ ItemModal
  const customSpaceHandler = () => {
    if (filteredItemDataList[selectedRowIndex]) {
      handleCheckboxChange(filteredItemDataList[selectedRowIndex]);
    }
  };

  // ใช้ Modal Keyboard Navigation Utility
  useModalKeyboardNavigation(
    showItemModal,
    filteredItemDataList,
    selectedRowIndex,
    setSelectedRowIndex,
    null, // ไม่ใช้ default onRowSelect
    handleItemClose,
    {
      customEnterHandler: () => onRowSelectItem(selectedItems),
      customSpaceHandler,
      enableSpace: true,
      includeHighlightedOnEnter: true,
      selectedItems: selectedItems
    }
  );

  useEffect(() => {
    if (Array.isArray(itemDataList)) {
      const normalized = itemDataList.map((item) => ({
        ...item,
        Item_Img: normalizeImageValue(item.Item_Img),
      }));
      setNormalizedItemDataList(normalized);
      setFilteredItemDataList(normalized);
    } else {
      setNormalizedItemDataList([]);
      setFilteredItemDataList([]);
    }
  }, [itemDataList]);

  useEffect(() => {
    if (showItemModal) {
      setSearchTerm("");
      setSelectedItems([]);
      setSelectedRowIndex(0); // รีเซ็ตการเลือกแถวเมื่อเปิด modal
      setSelectedGroup("");
      setFilteredItemDataList(normalizedItemDataList);
    }
  }, [showItemModal, normalizedItemDataList]);

  const handleCallInitializeFromForm = async () => {
    try {
      const items = await getDataWithComp(
        "Tb_Set_Item",
        "ORDER By Item_Code ASC",
        {
          fields: [
            "Item_Id",
            "Item_Code",
            "Item_Name",
            "Item_Img",
            "Item_Group",
            "Item_Status"
          ]
        }
      );
      const normalized = Array.isArray(items)
        ? items.map((item) => ({ ...item, Item_Img: normalizeImageValue(item.Item_Img) }))
        : [];
      setNormalizedItemDataList(normalized);
      setFilteredItemDataList(normalized);
      if (typeof setItemDataList === "function" && Array.isArray(items) && items.length > 0) {
        setItemDataList(items);
      }
      setShowAddItemModal(false);
    } catch (error) {
      console.error("Failed to reload items:", error);
      getAlert("FAILED", "ไม่สามารถโหลดรายการสินค้าได้");
    }
  };

  useEffect(() => {
    const normalizedTerm = searchTerm.toLowerCase();
    const filteredItems = normalizedItemDataList.filter((item) => {
      const matchesGroup =
        !selectedGroup || String(item.Item_Group) === selectedGroup;
      if (!matchesGroup) {
        return false;
      }
      return (
        item.Item_Code.toLowerCase().includes(normalizedTerm) ||
        item.Item_Name.toLowerCase().includes(normalizedTerm)
      );
    });
    setFilteredItemDataList(filteredItems);
    setSelectedRowIndex(0); // รีเซ็ตการเลือกแถวเมื่อข้อมูลเปลี่ยน
  }, [searchTerm, normalizedItemDataList, selectedGroup]);

  useEffect(() => {
    const loadItemGroups = async () => {
      if (!showItemModal || !normalizedItemDataList || normalizedItemDataList.length === 0) {
        setItemGroups([]);
        return;
      }

      const uniqueItemIds = Array.from(
        new Set(
          normalizedItemDataList
            .map((item) => item.Item_Id)
            .filter((itemId) => itemId !== undefined && itemId !== null)
        )
      );

      if (uniqueItemIds.length === 0) {
        setItemGroups([]);
        return;
      }

      try {
        const compId = window.localStorage.getItem("company");
        const idList = uniqueItemIds.join(",");
        let whereClause = `WHERE i.Item_Id IN (${idList})`;

        if (compId) {
          whereClause += ` AND i.Comp_Id = ${compId}`;
        }

        whereClause += ` AND g.Group_Status = 'Y' ORDER BY g.Group_Name ASC`;

        const groups = await getDataWithJoin(
          "DISTINCT g.Group_Id, g.Group_Name",
          "Tb_Set_ItmGroup AS g",
          "INNER JOIN Tb_Set_Item AS i ON g.Group_Id = i.Item_Group",
          whereClause
        );

        if (groups && groups.length > 0) {
          setItemGroups(groups);
        } else {
          setItemGroups([]);
        }
      } catch (error) {
        console.error("Failed to load item groups:", error);
        getAlert("FAILED", "ไม่สามารถโหลดหมวดหมู่สินค้าได้");
        setItemGroups([]);
      }
    };

    loadItemGroups();
  }, [showItemModal, normalizedItemDataList]);

  const handleSelectAll = (isChecked) => {
    if (isChecked) {
      setSelectedItems(filteredItemDataList);
    } else {
      setSelectedItems([]);
    }
  };

  const handleCheckboxChange = (item) => {
    setSelectedItems((prevSelected) => {
      const isSelected = prevSelected.some(
        (selected) => selected.Item_Id === item.Item_Id
      );

      const newSelectedItems = isSelected
        ? prevSelected.filter((selected) => selected.Item_Id !== item.Item_Id)
        : [...prevSelected, item];

      // อัปเดต selectedRowIndex เฉพาะเมื่อ item ถูกเลือก (checked)
      if (!isSelected) { // ถ้าเป็นการเลือก (ไม่ใช่การยกเลิก)
        const itemIndex = filteredItemDataList.findIndex(filteredItem => filteredItem.Item_Id === item.Item_Id);
        if (itemIndex !== -1) {
          setSelectedRowIndex(itemIndex);
        }
      }

      return newSelectedItems;
    });
  };

  return (
    <>
      <ItemModalView
        showItemModal={showItemModal}
        showAddItemModal={showAddItemModal}
        handleItemClose={handleItemClose}
        onOpenAddItem={() => setShowAddItemModal(true)}
        searchTerm={searchTerm}
        onSearchTermChange={setSearchTerm}
        selectedGroup={selectedGroup}
        onSelectedGroupChange={setSelectedGroup}
        itemGroups={itemGroups}
        controlHeight={CONTROL_HEIGHT}
        filteredItemDataList={filteredItemDataList}
        selectedItems={selectedItems}
        selectedRowIndex={selectedRowIndex}
        onSelectAll={handleSelectAll}
        onToggleItem={handleCheckboxChange}
        onConfirm={() => onRowSelectItem(selectedItems)}
        getImageSrc={buildImageSrc}
        keyboardInstructions={getKeyboardInstructions("สินค้า", true)}
      />

      <ItemModalAddItem
        open={showAddItemModal}
        onClose={() => setShowAddItemModal(false)}
        onCreated={handleCallInitializeFromForm}
        formData={addFormData}
        setFormData={setAddFormData}
      />
    </>
  );
};

export default ItemModal;
